import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Trophy, Sparkles, Star, Flame, Share2, Download, Copy, Check, Loader2 } from "lucide-react";
import { TIER_COLORS, EvolveTier } from "@/types/evolve";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  generateShareImage,
  getShareCaption,
  shareWithImage,
  downloadImage,
  getRandomQuote,
} from "@/utils/shareImageGenerator";

// Social media icons
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

interface TierUpCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  newTier: EvolveTier;
  totalWorkouts: number;
  userName?: string;
  referralCode?: string;
}

export function TierUpCelebration({ 
  isOpen, 
  onClose, 
  newTier, 
  totalWorkouts,
  userName = 'Evolver',
  referralCode,
}: TierUpCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [view, setView] = useState<'celebration' | 'share'>('celebration');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [copied, setCopied] = useState(false);
  
  const tierColor = TIER_COLORS[newTier] || TIER_COLORS['Seed'];
  const motivationalQuote = getRandomQuote();
  const caption = getShareCaption('tier', userName, totalWorkouts, newTier, referralCode);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      setView('celebration');
      setPreviewUrl(null);
      setImageBlob(null);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleShareClick = async () => {
    setIsGenerating(true);
    try {
      const blob = await generateShareImage({
        type: 'tier',
        userName,
        daysActive: totalWorkouts,
        tier: newTier,
        tierName: newTier,
      });
      setImageBlob(blob);
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setView('share');
    } catch (err) {
      console.error('Preview generation error:', err);
      toast.error('Unable to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNativeShare = async () => {
    if (!imageBlob) return;
    try {
      const shared = await shareWithImage(imageBlob, caption);
      if (!shared) {
        downloadImage(imageBlob);
        await navigator.clipboard.writeText(caption);
        toast.success('Image downloaded & caption copied!');
      } else {
        toast.success('Shared successfully!');
      }
    } catch (err) {
      toast.error('Unable to share. Please try again.');
    }
  };

  const handleDownload = () => {
    if (!imageBlob) return;
    downloadImage(imageBlob);
    toast.success('Image downloaded!');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      toast.success('Caption copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Unable to copy.');
    }
  };

  const shareToTwitter = () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-tier-up-twitter.png');
    const text = encodeURIComponent(caption);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'width=550,height=420');
    if (imageBlob) toast.success('Image downloaded! Attach it to your tweet.');
  };

  const shareToFacebook = () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-tier-up-facebook.png');
    const text = encodeURIComponent(caption);
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank', 'width=550,height=420');
    if (imageBlob) toast.success('Image downloaded! Attach it to your post.');
  };

  const shareToInstagram = async () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-tier-up-instagram.png');
    await navigator.clipboard.writeText(caption);
    toast.success('Image downloaded & caption copied! Open Instagram to share.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm bg-card border-primary/50 overflow-hidden [&>button]:hidden">
        {/* Confetti particles */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-[confetti_2s_ease-out_forwards]"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  top: '-10px',
                }}
              >
                {i % 3 === 0 ? (
                  <Star className="w-4 h-4 text-yellow-400" />
                ) : i % 3 === 1 ? (
                  <Sparkles className="w-4 h-4 text-primary" />
                ) : (
                  <Flame className="w-4 h-4 text-orange-500" />
                )}
              </div>
            ))}
          </div>
        )}

        {view === 'celebration' ? (
          <div className="text-center py-6 relative z-10">
            {/* Animated trophy */}
            <div className="relative mb-6">
              <div className={cn(
                "w-24 h-24 mx-auto rounded-full bg-gradient-to-br flex items-center justify-center",
                "animate-[tier-glow_2s_ease-in-out_infinite]",
                tierColor
              )}>
                <Trophy className="w-12 h-12 text-foreground animate-[bounce_1s_ease-in-out_2]" />
              </div>
              <div className="absolute -top-2 -right-2 animate-[spin_3s_linear_infinite]">
                <Sparkles className="w-8 h-8 text-yellow-400" />
              </div>
              <div className="absolute -bottom-2 -left-2 animate-[spin_3s_linear_infinite_reverse]">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
            </div>

            {/* Title */}
            <h2 className="font-display text-3xl font-bold mb-2 animate-[scale-in_0.5s_ease-out]">
              TIER UP!
            </h2>
            
            {/* New tier badge */}
            <div className={cn(
              "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r text-lg font-bold mb-4",
              "animate-[pulse_2s_ease-in-out_infinite]",
              tierColor
            )}>
              <Flame className="w-5 h-5" />
              {newTier}
            </div>

            {/* Stats */}
            <p className="text-muted-foreground mb-4">
              You've completed <span className="text-primary font-bold">{totalWorkouts} workouts</span>
            </p>

            {/* Motivational message */}
            <p className="text-sm text-muted-foreground italic mb-6 px-2">
              "{getMotivationalMessage(newTier)}"
            </p>

            {/* Share Button */}
            <Button
              onClick={handleShareClick}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold mb-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Achievement
                </>
              )}
            </Button>

            <Button variant="ghost" onClick={onClose} className="w-full text-muted-foreground">
              Keep Evolving 💪
            </Button>
          </div>
        ) : (
          /* Share Preview View */
          <div className="relative z-10 p-4 pt-6 flex flex-col">
            {/* Image Preview */}
            {previewUrl && (
              <div className="rounded-xl overflow-hidden border border-white/10 mb-4 shadow-xl">
                <img 
                  src={previewUrl} 
                  alt="Share preview" 
                  className="w-full aspect-square object-cover"
                />
              </div>
            )}

            {/* Social buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Button
                onClick={shareToFacebook}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-[#1877F2]/10 border-[#1877F2]/30 hover:bg-[#1877F2]/20 text-[#1877F2]"
              >
                <FacebookIcon />
                <span className="text-[10px]">Facebook</span>
              </Button>
              <Button
                onClick={shareToTwitter}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-black/50 border-white/10 hover:bg-black/70 text-white"
              >
                <TwitterIcon />
                <span className="text-[10px]">X</span>
              </Button>
              <Button
                onClick={shareToInstagram}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-pink-500/30 hover:from-purple-500/30 hover:to-pink-500/30 text-pink-400"
              >
                <InstagramIcon />
                <span className="text-[10px]">Instagram</span>
              </Button>
              <Button
                onClick={handleDownload}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 border-white/10 hover:bg-white/10"
              >
                <Download className="w-4 h-4" />
                <span className="text-[10px]">Download</span>
              </Button>
            </div>

            {/* Copy caption */}
            <Button
              onClick={handleCopy}
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-foreground"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Caption Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Caption
                </>
              )}
            </Button>

            {/* Done button */}
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full mt-2 text-muted-foreground hover:text-foreground"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function getMotivationalMessage(tier: EvolveTier): string {
  const messages: Record<EvolveTier, string> = {
    'Seed': "Every legend starts somewhere. You've planted the seed.",
    'Spark': "You showed up. That's more than most. The spark is lit.",
    'Flame': "Two down. You're building something. Feed the flame.",
    'Ember': "Three workouts. Habit forming. The ember burns bright.",
    'Blaze': "Five complete. You're on fire now. No stopping this blaze.",
    'Inferno': "Seven strong. This is becoming who you are. INFERNO.",
    'Pre-Legend': "Double digits. You're at the gates of Legend status.",
    'Legend': "LEGEND STATUS ACHIEVED. You're in the top 1% now.",
    'Titan': "25 workouts. You're built different. TITAN mode activated.",
    'Immortal': "50 workouts. Your discipline is IMMORTAL.",
    'Immortal II': "100 workouts. You are the storm others fear.",
    'Immortal III': "200 workouts. Living legend. Absolute beast.",
    'Ascended': "300 workouts. You've ASCENDED beyond mortal limits.",
    'Ascended II': "500 workouts. Transcendent. Unstoppable.",
    'Mythic': "1000 workouts. MYTHIC status. They'll write stories about you.",
    'Eternal': "2500 workouts. ETERNAL. You are the definition of dedication.",
  };
  return messages[tier] || "Keep pushing. Evolution never stops.";
}
